import { useState, useEffect } from 'react';

const API_BASE = '/api';

// Enhanced user ID management that works with authentication
const getUserId = () => {
  let userId = localStorage.getItem('cf_user_id');
  if (!userId) {
    userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('cf_user_id', userId);
  }
  return userId;
};

// Enhanced headers that work with both authenticated and anonymous users
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-User-ID': getUserId()
});

// Make authenticated request - handles both cookie-based auth and header-based anonymous
const makeRequest = async (url, options = {}) => {
  const config = {
    ...options,
    headers: {
      ...getHeaders(),
      ...options.headers
    },
    credentials: 'include' // Always include cookies for authenticated users
  };
  
  return fetch(url, config);
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFavorites = async () => {
    try {
      const response = await makeRequest(`${API_BASE}/favorites`);
      const data = await response.json();
      setFavorites(data.map(f => f.property_id));
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFavorites();
  }, []);

  const toggleFavorite = async (propertyId) => {
    const isFavorited = favorites.includes(propertyId);
    
    try {
      if (isFavorited) {
        await makeRequest(`${API_BASE}/favorites/${propertyId}`, {
          method: 'DELETE'
        });
        setFavorites(prev => prev.filter(id => id !== propertyId));
      } else {
        await makeRequest(`${API_BASE}/favorites`, {
          method: 'POST',
          body: JSON.stringify({ propertyId })
        });
        setFavorites(prev => [...prev, propertyId]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const clearAllFavorites = async () => {
    try {
      for (const propertyId of favorites) {
        await makeRequest(`${API_BASE}/favorites/${propertyId}`, {
          method: 'DELETE'
        });
      }
      setFavorites([]);
    } catch (error) {
      console.error('Error clearing favorites:', error);
    }
  };

  const isFavorite = (propertyId) => favorites.includes(propertyId);

  return {
    favorites,
    loading,
    toggleFavorite,
    clearAllFavorites,
    isFavorite
  };
};

export const useNotes = () => {
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);

  const loadNotes = async () => {
    try {
      const response = await makeRequest(`${API_BASE}/notes`);
      const data = await response.json();
      const notesObj = {};
      data.forEach(n => {
        notesObj[n.property_id] = n.note;
      });
      setNotes(notesObj);
    } catch (error) {
      console.error('Error loading notes:', error);
      setNotes({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  const saveNote = async (propertyId, note) => {
    try {
      await makeRequest(`${API_BASE}/notes/${propertyId}`, {
        method: 'PUT',
        body: JSON.stringify({ note })
      });

      if (!note || !note.trim()) {
        setNotes(prev => {
          const updated = { ...prev };
          delete updated[propertyId];
          return updated;
        });
      } else {
        setNotes(prev => ({ ...prev, [propertyId]: note }));
      }
      return true;
    } catch (error) {
      console.error('Error saving note:', error);
      return false;
    }
  };

  const deleteNote = async (propertyId) => {
    try {
      await makeRequest(`${API_BASE}/notes/${propertyId}`, {
        method: 'DELETE'
      });
      setNotes(prev => {
        const updated = { ...prev };
        delete updated[propertyId];
        return updated;
      });
      return true;
    } catch (error) {
      console.error('Error deleting note:', error);
      return false;
    }
  };

  const getNote = (propertyId) => notes[propertyId] || null;

  const hasNote = (propertyId) => !!notes[propertyId];

  return {
    notes,
    loading,
    saveNote,
    deleteNote,
    getNote,
    hasNote
  };
};

export const useTags = () => {
  const [tags, setTags] = useState({});
  const [loading, setLoading] = useState(true);

  const loadTags = async () => {
    try {
      const response = await makeRequest(`${API_BASE}/tags`);
      const data = await response.json();
      const tagsObj = {};
      data.forEach(tag => {
        tagsObj[tag.id] = tag;
      });
      setTags(tagsObj);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const createNewTag = async (name, color = '#3B82F6') => {
    const tagId = 'tag_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    try {
      await makeRequest(`${API_BASE}/tags`, {
        method: 'POST',
        body: JSON.stringify({ id: tagId, name, color })
      });
      
      const newTag = {
        id: tagId,
        name: name.trim(),
        color: color,
        properties: [],
        created_at: Date.now()
      };
      
      setTags(prev => ({ ...prev, [tagId]: newTag }));
      return tagId;
    } catch (error) {
      console.error('Error creating tag:', error);
      return null;
    }
  };

  const updateTag = async (tagId, updates) => {
    try {
      await makeRequest(`${API_BASE}/tags/${tagId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      
      setTags(prev => ({
        ...prev,
        [tagId]: { ...prev[tagId], ...updates, id: tagId }
      }));
      
      return true;
    } catch (error) {
      console.error('Error updating tag:', error);
      return false;
    }
  };

  const deleteTag = async (tagId) => {
    try {
      await makeRequest(`${API_BASE}/tags/${tagId}`, {
        method: 'DELETE'
      });
      
      setTags(prev => {
        const newTags = { ...prev };
        delete newTags[tagId];
        return newTags;
      });
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  const addPropertyToTag = async (propertyId, tagId) => {
    try {
      await makeRequest(`${API_BASE}/property-tags`, {
        method: 'POST',
        body: JSON.stringify({ propertyId, tagId })
      });
      
      setTags(prev => {
        const tag = prev[tagId];
        if (tag && !tag.properties.includes(propertyId)) {
          return {
            ...prev,
            [tagId]: {
              ...tag,
              properties: [...tag.properties, propertyId]
            }
          };
        }
        return prev;
      });
    } catch (error) {
      console.error('Error adding property to tag:', error);
    }
  };

  const removePropertyFromTag = async (propertyId, tagId) => {
    try {
      await makeRequest(`${API_BASE}/property-tags`, {
        method: 'DELETE',
        body: JSON.stringify({ propertyId, tagId })
      });
      
      setTags(prev => {
        const tag = prev[tagId];
        if (tag) {
          return {
            ...prev,
            [tagId]: {
              ...tag,
              properties: tag.properties.filter(id => id !== propertyId)
            }
          };
        }
        return prev;
      });
    } catch (error) {
      console.error('Error removing property from tag:', error);
    }
  };

  const getPropertyTags = async (propertyId) => {
    try {
      const response = await makeRequest(`${API_BASE}/property-tags/${propertyId}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting property tags:', error);
      return [];
    }
  };

  const getAllTags = () => Object.values(tags);

  return {
    tags,
    loading,
    createNewTag,
    updateTag,
    deleteTag,
    addPropertyToTag,
    removePropertyFromTag,
    getPropertyTags,
    getAllTags
  };
};
